import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { IconCaretDownFilled } from "@tabler/icons-react";

// Components
import { MButton } from "@/components/ui/atoms/MButtons";
import MHeader from "@/components/ui/organisms/MHeader";
import { AutoCompleteInput } from "@/components/ui/organisms/AutoCompleteInput";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { MDateInputField } from "@/components/ui/molecules/MDateInputField";
import CriticalReminderToggle from "@/components/ui/molecules/CriticalReminderToggle";

// Services
import { typeAheadService } from "@/services/type-ahead.service";

// Types
import { IVaccination } from "@/types/vaccination";
import { TypeAheadCategory, getCategoryKey } from "@/types/typeAhead";

// Utils
import {
  addYears,
  isPast,
  subDays,
  isBefore,
  startOfDay,
  endOfDay,
} from "date-fns";
import {
  DEFAULT_PAST_START_DATE,
  DEFAULT_TIME,
  DEFAULT_YEARS_AHEAD,
} from "@/utils/constants";
import MTimeInputField from "@/components/ui/atoms/MTimeInputField";
import { getTimezone, inputFieldTime, updateTime } from "@/utils/date-utils";
import { getMemberInfo } from "@/hooks/useMembers";
import { useVaccinations } from "@/hooks/useVaccinations";
import { useReminders } from "@/hooks/useReminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { getReminderOptions, reminderOptions } from "@/utils/reminder-utils";
import { goBackOneLevel } from "@/utils/nav-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { getLearnMoreContent } from "@/utils/vaccination-utils";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const VaccinationFormPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const memberInfo = getMemberInfo();
  const { derivedMemberType } = memberInfo;
  const vaccination = location.state?.vaccination;
  const isAdding = !vaccination;

  const [name, setName] = useState("");
  const [hasReceived, setHasReceived] = useState<boolean | null>(
    vaccination ? vaccination.taken : null
  );
  const [dateTaken, setDateTaken] = useState<Date | undefined>(
    vaccination?.takenOn
      ? typeof vaccination.takenOn === "string"
        ? new Date(vaccination.takenOn)
        : vaccination.takenOn
      : undefined
  );
  const [dueDate, setDueDate] = useState<Date | undefined>(
    vaccination?.dueDate
      ? typeof vaccination.dueDate === "string"
        ? new Date(vaccination.dueDate)
        : vaccination.dueDate
      : undefined
  );
  const [reminderDays, setReminderDays] = useState<number | undefined>(
    vaccination?.reminderDays ?? undefined
  );
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState<boolean>(false);
  const [search, setSearch] = useState(vaccination?.name || "");
  const [fetched, setFetched] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // We only need to track if the form has validation errors, not the specific errors
  const [isFormValid, setIsFormValid] = useState(false);
  const [reminderTime, setReminderTime] = useState(
    reminderDays !== undefined
      ? inputFieldTime(vaccination?.dueDate, null, false, vaccination?.timezone)
      : undefined
  );
  const [isCritical, setIsCritical] = useState<boolean>(
    vaccination ? vaccination.tier === "CRITICAL" : false
  );

  const { addVaccination, updateVaccination } = useVaccinations(memberInfo.id);
  const { totalCount: reminderTotalCount, fetchReminders } = useReminders(
    memberInfo.id
  );

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);

  // Handle vaccination data from location state when editing
  useEffect(() => {
    if (location.state?.vaccination) {
      const editVaccination = location.state.vaccination;
      setName(editVaccination.name || "");
      setSearch(editVaccination.name || "");
      setHasReceived(editVaccination.taken);
      setDateTaken(
        editVaccination.takenOn
          ? typeof editVaccination.takenOn === "string"
            ? new Date(editVaccination.takenOn)
            : editVaccination.takenOn
          : undefined
      );
      setDueDate(
        editVaccination.dueDate
          ? typeof editVaccination.dueDate === "string"
            ? new Date(editVaccination.dueDate)
            : editVaccination.dueDate
          : undefined
      );
      setReminderDays(editVaccination.reminderDays);
      if (reminderDays !== undefined) {
        setReminderTime(
          inputFieldTime(
            editVaccination.dueDate,
            null,
            false,
            editVaccination.timezone
          )
        );
      }
      setFetched(true); // Prevent dropdown from opening by marking as fetched
    } else if (location.state?.vaccineName) {
      setName(location.state.vaccineName);
      setSearch(location.state.vaccineName);
      setFetched(true); // Prevent dropdown from opening by marking as fetched
    }
  }, [location.state]);

  // Fetch vaccination options
  const fetchOptions = async (query: string) => {
    if (fetched) {
      return;
    }

    setLoading(true);
    try {
      const res = await typeAheadService.searchInCategory(
        getCategoryKey(TypeAheadCategory.VACCINATION, memberInfo),
        { search: query }
      );
      setOptions(res);
      setFetched(true);
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle saving a vaccination (add or edit)
  const handleSaveVaccination = async (vaccination: IVaccination) => {
    try {
      if (reminderDays !== undefined && reminderTime === undefined) {
        vaccination.dueDate = updateTime(dueDate, DEFAULT_TIME);
      }

      // Check if this is first reminder BEFORE adding
      const isFirstReminder = reminderTotalCount === 0;

      if (isAdding) {
        await addVaccination(vaccination);
      } else {
        await updateVaccination(vaccination);
      }

      // Track vaccination completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_VACCINATION_COMPLETE,
        ...(isFirstReminder && isAdding && { action: "first_reminder_added" }),
      });

      // Determine the target path based on the current location
      let targetPath: string;

      if (location.pathname.includes("suggested-vaccines")) {
        // Go back two levels for suggested vaccines
        targetPath = goBackOneLevel(location.pathname, 2);
      } else {
        // Go back one level for regular add/edit
        targetPath = goBackOneLevel(location.pathname);
      }

      // If the vaccination is marked as taken, append the taken tab parameter
      const tabParam = vaccination.taken ? "?tab=taken" : "?tab=pending";
      navigate(`${targetPath}${tabParam}`);
    } catch (error) {
      console.error("Error saving vaccination:", error);
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  };

  // Date handling
  const handleDateTakenChange = (date: Date) => {
    setDateTaken(date);
  };

  const handleDueDateChange = (date: Date) => {
    setDueDate(date);
  };

  const handleReminderTimeChange = (time: string) => {
    setReminderTime(time);
    setDueDate(updateTime(dueDate, time));
  };

  // Validate form
  const validateForm = (silent: boolean = false) => {
    // Check for validation errors
    if (
      !name ||
      hasReceived === null ||
      hasReceived === undefined ||
      (hasReceived === false && !dueDate) ||
      (hasReceived === false &&
        (reminderDays === null || reminderDays === undefined)) ||
      (hasReceived === false && !reminderTime)
    ) {
      // Only show toast if not in silent mode
      if (
        !silent &&
        (!name || hasReceived === null || hasReceived === undefined)
      ) {
        showToast({
          message: t("vaccinations.form.validation.description"),
          variant: "error",
        });
      }
      return false;
    }

    if (hasReceived === false) {
      if (isPast(endOfDay(dueDate))) {
        // Only show toast if not in silent mode
        if (!silent) {
          showToast({
            message: t("vaccinations.dueDatePast"),
            variant: "error",
          });
        }
        return false;
      }

      if (
        reminderDays !== null &&
        reminderDays !== undefined &&
        isPast(subDays(dueDate, reminderDays))
      ) {
        // Only show toast if not in silent mode
        if (!silent) {
          showToast({
            message: t("vaccinations.reminderDaysPast"),
            variant: "error",
          });
        }
        return false;
      }
    }
    return true;
  };

  // Get form data
  const getFormData = (): IVaccination => ({
    name,
    taken: hasReceived,
    takenOn: dateTaken,
    dueDate,
    reminderDays,
    custom: !options.includes(name),
    updatedAt: new Date(),
    tier: isCritical ? "CRITICAL" : "ROUTINE",
  });

  // Save vaccination
  const handleSave = async () => {
    if (validateForm()) {
      const formData = getFormData();
      formData.timezone = getTimezone();
      await handleSaveVaccination({ ...formData, uid: vaccination?.uid });
    }
  };

  // Validate form whenever relevant data changes
  useEffect(() => {
    // Check form validity using the same logic as in validate method
    const isValid =
      Boolean(name) &&
      hasReceived !== null &&
      hasReceived !== undefined &&
      !(hasReceived === false && !dueDate) &&
      !(
        hasReceived === false &&
        (reminderDays === null || reminderDays === undefined)
      ) &&
      !(hasReceived === false && !reminderTime) &&
      !(
        hasReceived === false &&
        dueDate &&
        isBefore(dueDate, startOfDay(new Date()))
      ) &&
      !(
        hasReceived === false &&
        reminderDays !== null &&
        reminderDays !== undefined &&
        dueDate &&
        reminderTime &&
        // Ensure reminder date is before due date
        (isBefore(
          updateTime(subDays(dueDate, reminderDays), reminderTime),
          endOfDay(dueDate)
        ) === false ||
          // Ensure reminder date is after current date/time
          isBefore(
            new Date(),
            updateTime(subDays(dueDate, reminderDays), reminderTime)
          ) === false)
      );

    setIsFormValid(isValid);
  }, [name, hasReceived, dateTaken, dueDate, reminderDays, reminderTime]);

  const handleSetReminderDays = (days: number) => {
    setReminderDays(days);
    // Always set default time when reminder days are selected
    if (!reminderTime) {
      handleReminderTimeChange(DEFAULT_TIME);
    }
  };

  return (
    <div className="profile-page-root">
      {/* Form Header */}
      <MHeader
        module="add_vaccine"
        memberInfo={memberInfo}
        learnMoreContent={getLearnMoreContent(derivedMemberType, t)}
      />
      <MSpacer />

      {/* Vaccination Form */}
      <div className="profile-input-container mb-[80px]">
        {/* Vaccination Name */}
        <AutoCompleteInput
          label={t("vaccinations.form.name")}
          data={options}
          getOptionLabel={(item) => item}
          onSelect={(item) => {
            const selectedValue = item as string;
            setName(selectedValue);
            setSearch(selectedValue); // Keep search and name in sync
          }}
          placeholder={t("common.enterOrChoose")}
          value={search}
          onChange={(e) => {
            setSearch(e);
            setName(e); // Keep name updated with custom input

            // Always reset fetched state when the value is empty or very short
            // This ensures new searches trigger properly after deleting
            if (e.length <= 2) {
              setFetched(false);
              setOptions([]);
            }
            // Reset fetched state when search term changes significantly
            else if (
              e !== search &&
              (!e.startsWith(search) || e.length < search.length)
            ) {
              setFetched(false);
              // Reset options when search term changes completely
              if (
                !e.startsWith(search.substring(0, 2)) &&
                !search.startsWith(e.substring(0, 2))
              ) {
                setOptions([]);
              }
            }
          }}
          onSearch={fetchOptions}
          className="w-full"
          loading={loading}
          aria-label={t("vaccinations.form.name")}
          initialValue={name || ""}
          allowCustomInput={true}
          showAllOnFocus={true}
        />

        {/* Have you received this vaccination? */}
        <div className="field-container">
          <label className="profile-input-label">
            {t("vaccinations.form.received")}
          </label>
          <div className="action-buttons-container">
            <MButton
              variant={
                hasReceived === null
                  ? "outline"
                  : hasReceived === false
                  ? "filled"
                  : "outline"
              }
              size="md"
              text={t("common.no")}
              onClick={() => setHasReceived(false)}
              aria-pressed={hasReceived === false}
              className="flex-1"
            />
            <MButton
              variant={hasReceived === true ? "filled" : "outline"}
              size="md"
              text={t("common.yes")}
              onClick={() => setHasReceived(true)}
              aria-pressed={hasReceived === true}
              className="flex-1"
            />
          </div>
        </div>

        {/* Conditional fields based on Yes/No selection */}
        {hasReceived === true && (
          <MDateInputField
            label={t("vaccinations.form.dateTaken")}
            value={dateTaken}
            placeholder={t("vaccinations.form.notAnswered")}
            size="md"
            showLabel={true}
            variant="Underlined"
            onDateChange={handleDateTakenChange}
            minDate={
              memberInfo.dobYear && memberInfo.dobMonth
                ? new Date(
                    memberInfo.dobYear,
                    memberInfo.dobMonth - 1,
                    memberInfo.dobDay || 1
                  )
                : DEFAULT_PAST_START_DATE
            }
            maxDate={new Date()}
          />
        )}

        {hasReceived === false && (
          <>
            <MDateInputField
              label={t("vaccinations.form.dueDate")}
              value={dueDate}
              placeholder={t("vaccinations.form.notAnswered")}
              size="md"
              showLabel={true}
              variant="Underlined"
              mandatory={true}
              onDateChange={handleDueDateChange}
              minDate={new Date()}
              maxDate={addYears(new Date(), DEFAULT_YEARS_AHEAD)}
            />

            {/* Wrap MInputField in a div to make it clickable */}
            <div
              className="cursor-pointer w-full"
              onClick={() => setReminderDrawerOpen(true)}
            >
              <MInputField
                label={t("vaccinations.form.remindMe")}
                placeholder={t("vaccinations.form.notAnswered")}
                value={
                  reminderOptions(t).find(
                    (option) => option.value === reminderDays
                  )?.label || ""
                }
                // Make input read-only since we're using the drawer for selection
                onChange={() => {}}
                icon={<IconCaretDownFilled />}
                showIcon={true}
                disabled={false}
                mandatory={true}
              />
            </div>

            <MTimeInputField
              label={t("vaccinations.form.reminderTime")}
              value={reminderTime}
              onChange={handleReminderTimeChange}
              showLabel={true}
              variant="Underlined"
              size="md"
              mandatory={true}
            />

            {/* Selection Drawer for Reminder Days */}
            <MDropDownDrawer
              open={reminderDrawerOpen}
              onClose={() => setReminderDrawerOpen(false)}
              title={t("vaccinations.form.reminderDays")}
              items={getReminderOptions(dueDate, t)}
              onSelect={(value) => handleSetReminderDays(Number(value))}
            />

            <CriticalReminderToggle
              checked={isCritical}
              onChange={setIsCritical}
            />
          </>
        )}
      </div>

      {/* Save Button */}
      <div className="confirm-footer">
        <MButton
          onClick={handleSave}
          state={isFormValid ? "default" : "disabled"}
          variant="filled"
          size="lg"
          className="w-full"
          text={t("common.done")}
          disabled={!isFormValid}
        />
      </div>
    </div>
  );
};

export default VaccinationFormPage;
